<template>
    <div>
        <v-dialog
                v-model="stripeDialog"
                persistent
                width="400"
        >
            <v-card
                    color="primary"
                    dark
            >
                <v-card-text class="pa-6">
                    <h3 class="text-h6">{{ $t('headings.Please wait while we verify your card') }}</h3>
                    <v-progress-linear
                            indeterminate
                            color="white"
                            class="mb-6 mt-6"
                    ></v-progress-linear>
                </v-card-text>
            </v-card>
        </v-dialog>

        <div v-if="user && user.stripe_subscription">
            <v-list-item v-if="user && user.stripe_subscription">
                <v-list-item-title>{{ $t('headings.Subscription details') }}</v-list-item-title>
                <v-list-item-content>
                    <v-list-item-subtitle v-if="user.stripe_subscription.cancel_at === null">{{ $t("Next renewal due") }}: {{ user.stripe_subscription.current_period_start }}</v-list-item-subtitle>
                    <v-list-item-subtitle>{{ $t("Valid to") }}: {{ user.stripe_subscription.current_period_end }}</v-list-item-subtitle>
                    <v-list-item-subtitle v-if="user.stripe_subscription.cancel_at">{{ $t("Canceled from") }}: {{ user.stripe_subscription.cancel_at }}</v-list-item-subtitle>
                </v-list-item-content>
            </v-list-item>

            <v-btn @click="renewSubscription" v-if="user.stripe_subscription.cancel_at" small>{{ $t('buttons.Renew future payments') }}</v-btn>
        </div>
        <div v-if="authenticated() && ((user && user.stripe_subscription && user.stripe_subscription.ended_at) || !!user.stripe_subscription === false)">

            <p class="body-1 mt-6">{{ $t("You must have an active subscription to receive alerts, purchase new products, or activate tags") }}.</p>

            <IndividualsSummary />

            <p class="body-1">{{ $t("Please enter your card details below to subscribe for {subscription} per year, and claim a free bundle worth {price}", {subscription: subscriptionPriceWithCurrency, price: bundlePrice}) }}. {{ $t("Your card details will be stored securely with our payment gateway") }}.</p>

          <div v-if="coupon">
            <p class="text-h4 text-center">{{subscriptionDiscount}}<span class="text-overline ml-3">first years subscription</span><br><span class="text-h4"><span class="red--text" style="text-decoration: line-through">{{ subscriptionPriceWithCurrency }}</span> {{ reducedPrice }}</span></p>
          </div>

            <div v-else>
              <p class="text-h2 text-center"><span class="text-overline">first years subscription</span> <span class="text-h4">{{ subscriptionPriceWithCurrency }}</span></p>
              <p class="body-1">{{ $t("If you have a promo code please validate it here, before you enter your payment details") }}.</p>

              <ValidationObserver ref="promoObserver">
                <ValidationProvider vid="promo_code" :name="$t('labels.Promo code')" v-slot="{ errors }">
                  <div class="d-flex align-top">
                    <v-text-field
                        v-model="promo_code"
                        :error-messages="errors[0]"
                        type="text"
                        filled
                        dense
                        max-width="200px"
                        prepend-icon="mdi-ticket-percent"
                        :label="$t('labels.Promo code')"
                        :loading="validatingPromo"
                    >
                      <template v-slot:append>
                        <v-icon v-if="promoValid" color="green">mdi-check-bold</v-icon>
                      </template>
                    </v-text-field>
                    <v-btn class="ml-1" color="primary" elevation="0" height="53" @click="validatePromo">{{ $t('buttons.Validate') }} <v-icon color="white">mdi-chevron-right</v-icon></v-btn>
                  </div>

                </ValidationProvider>
              </ValidationObserver>
              <p class="body-1">{{ $t("Please enter your card details below for your {subscription} annual subscription", {subscription: subscriptionPriceWithCurrency}) }}.</p>
            </div>

            <div id="payment-element"></div>
            <v-btn class="mt-4" block elevation="0" color="primary" @click='pay' :loading="loading" :disabled='stripeDialog === true'>{{ $t('buttons.Pay with card') }}</v-btn>
        </div>
        <div v-else>
            <p class="body-1 mt-6">{{ $t("Please sign in to purchase products") }}.</p>
            <p class="body-1">{{ $t("You must be signed up as an individual and have an active subscription before you can purchase products, activate tags, or receive alerts") }}.</p>
        </div>

        <v-snackbar v-model="snack" :timeout="3000" :color="snackColor">
            {{ snackText }}
            <v-btn text @click="snack = false">{{ $t('buttons.Close') }}</v-btn>
        </v-snackbar>
    </div>
</template>

<script>

    import { loadStripe } from '@stripe/stripe-js'
    import { stripeService, userService } from '../services'
    import { mapState, mapGetters, mapActions } from 'vuex'

    import axios from 'axios';
    import { store } from '../store';
    import {i18n} from "../i18n";
    import IndividualsSummary from "./IndividualsSummary"
    import * as Sentry from "@sentry/vue";

    //const stripe = window.Stripe(process.env.VUE_APP_STRIPE_KEY, {locale: i18n.locale === 'es' ? 'es' : 'en'});

    export default {
        name: "MembershipForm",
        props: ['locale'],
        components: {
          IndividualsSummary
        },
        data: () => ({
            promo_code: '',
            promoValid: false,
            validatingPromo: false,
            coupon: null,
            mountStripeElement: true,
            stripeKey: process.env.VUE_APP_STRIPE_KEY,
            complete: false,
            declined: false,
            stripeDialog: false,
            snack: false,
            snackColor: '',
            snackText: '',
            freeProduct: null,
            token: null,
            stripe: null,
            elementsObject: null,
            clientSecretObject: null,
            paymentFormLoaded: false,
            loading: false,
            processing: false,
        }),
        mounted () {

            if (this.user.organisations && this.user.organisations.length > 0 && this.user.organisations[0].coupon) {
              this.coupon = this.user.organisations[0].coupon
            }

            // this.stripe = window.Stripe(process.env.VUE_APP_STRIPE_KEY, {locale: this.locale === 'es' ? 'es' : 'en'});

            // this.mountStripeElement = true;

            this.loadPaymentForm()

            const instance = axios.create({
              baseURL: process.env.VUE_APP_NEOTAG_HOSTNAMES.split(",").includes(window.location.hostname) ? process.env.VUE_APP_API_HOST_NEOTAG : process.env.VUE_APP_API_HOST,
              headers: {'Accept-Language': i18n.locale}
            });

            instance
                .get('/api/products/slug/free-bundle' )
                .then(response => {
                    this.freeProduct = response.data.data
                })
                .catch(error => {
                    console.log(error)
                })

            // stripeService.subscriptionIntent().then(intent => {
            //   this.clientSecret = intent.client_secret
            //   console.log(this.clientSecret)
            //
            //   const options = {
            //     clientSecret: this.clientSecret,
            //     // Fully customizable with appearance API.
            //     appearance: {
            //       theme: this.brand === 'neotag' ? 'night' : 'stripe'
            //     }
            //   };
            //
            //   this.elements = this.stripe.elements(options);
            //
            //   this.card = this.elements.create('payment');
            //   this.card.mount(this.$refs.card);
            // })
        },
        watch: {
          locale (val){
            this.stripe = window.Stripe(process.env.VUE_APP_STRIPE_KEY, {locale: val === 'es' ? 'es' : 'en'});

            stripeService.subscriptionIntent().then(intent => {
              this.clientSecret = intent.client_secret
              console.log(this.clientSecret)

              const options = {
                clientSecret: this.clientSecret,
                // Fully customizable with appearance API.
                appearance: {
                  theme: this.brand === 'neotag' ? 'night' : 'default'
                }
              };

              this.elements = this.stripe.elements(options);

              this.card = this.elements.create('payment');
              this.card.mount(this.$refs.card);
            })
          }
        },
        computed: {
            ...mapState({
                user: state => state.account.user,
                brand: state => state.brand,
                brandName: state => state.brandName
            }),
            ...mapGetters('account', ['hasMembership']),
            subscriptionDiscount() {
              if (this.coupon) {
                if (this.brand === 'neotag') {
                  if (this.coupon.amount_off === (process.env.VUE_APP_SUBSCRIPTION_EUR * 100)) {
                    return 'FREE '
                  }

                  // let discount = this.organisation.coupon.amount_off / 100
                  // return ((discount / process.env.VUE_APP_SUBSCRIPTION_EUR) * 100) + '% off '
                  return '€' + (this.coupon.amount_off / 100) + ' ' + i18n.t('off')
                }
                else {
                  if (this.coupon.amount_off === (process.env.VUE_APP_SUBSCRIPTION_GBP * 100)) {
                    return 'FREE '
                  }

                  // let discount = this.organisation.coupon.amount_off / 100
                  // return ((discount / process.env.VUE_APP_SUBSCRIPTION_GBP) * 100) + '% off '
                  return '£' + (this.coupon.amount_off / 100) + ' ' + i18n.t('off')
                }
              }
              return null
            },
            subscriptionPrice() {
              if (this.brand === 'neotag') {
                return process.env.VUE_APP_SUBSCRIPTION_EUR
              }

              return process.env.VUE_APP_SUBSCRIPTION_GBP
            },
            subscriptionPriceWithCurrency() {
              if (this.brand === 'neotag') {
                return '€' + process.env.VUE_APP_SUBSCRIPTION_EUR
              }
              else {
                return '£' + process.env.VUE_APP_SUBSCRIPTION_GBP
              }
            },
            reducedPrice() {
              if (this.coupon) {
                if (this.brand === 'neotag') {
                  return '€' + (process.env.VUE_APP_SUBSCRIPTION_EUR - (this.coupon.amount_off / 100))
                }
                else {
                  return '£' + (process.env.VUE_APP_SUBSCRIPTION_GBP - (this.coupon.amount_off / 100))
                }
              }
              return null
            },
            bundlePrice() {
              if (this.brand === 'neotag') {
                return '€' + process.env.VUE_APP_BUNDLE_EUR
              }

              return '£' + process.env.VUE_APP_BUNDLE_GBP
            }
        },
        methods: {
            ...mapActions('account', ['updateSelf', 'updateMembership', 'updateSubscription']),
            ...mapActions('basket', ['addSilent']),
            async loadPaymentForm () {
              this.stripe = await loadStripe(this.stripeKey);

              let couponId = this.coupon ? this.coupon.id : null;

              stripeService.createSubscription(this.brand, couponId).then(response => {

                this.clientSecretObject = response.client_secret
                console.log(this.clientSecretObject)
                let clientSecret = this.clientSecretObject

                const appearance = {
                  theme: this.brand.neotag ? 'night' : 'default'
                };
                const options = { /* options */ };
                let elements = this.stripe.elements({ clientSecret, appearance });
                this.elementsObject = elements
                console.log(this.elementsObject)
                const paymentElement = elements.create('payment', options);
                paymentElement.mount('#payment-element');
                this.paymentFormLoaded = true
              }).catch(function (error) {
                console.log(error)
              });
            },
            validatePromo() {

              // remove errors
              this.$refs.promoObserver.reset();

              this.validatingPromo = true
              this.promoValid = false
              this.coupon = null

              const instance = axios.create({
                baseURL: process.env.VUE_APP_NEOTAG_HOSTNAMES.split(",").includes(window.location.hostname) ? process.env.VUE_APP_API_HOST_NEOTAG : process.env.VUE_APP_API_HOST
              });

              instance
                  .post('/api/stripe/validate-promo-code', {
                    promo_code: this.promo_code,
                    brand: this.brand
                  })
                  .then(response => {
                    if (response.status === 200) {
                      this.promoValid = true
                      this.coupon = response.data.coupon
                      this.loadPaymentForm ()
                      console.log(response.data.coupon)
                    }
                  })
                  .catch(error => {
                    console.log(error)
                    if (error.response.status === 404) {

                      this.$refs.promoObserver.setErrors(error.response.data.errors);

                    }


                  })
                  .finally(() => {
                    this.validatingPromo = false
                  })

            },
            authenticated () {
                return store.state.authenticated
            },
            async pay () {
              this.loading = true

              let elements = this.elementsObject

              let domain = (new URL(window.location));

              let that = this;

              this.stripe.confirmPayment(
                  {
                    elements,
                    redirect: 'if_required',
                    confirmParams: {
                      return_url: 'https://' + domain.hostname + "/account",
                    }
                  }
              ).then(function(result) {
                console.log(result)

                if (result.paymentIntent) {
                  switch (result.paymentIntent.status) {
                    case 'succeeded':

                      stripeService.confirmSubscription().then(response => {
                        console.log(response)
                        if (response) {
                          let subscription = response.data.stripe_subscription
                          let product = that.freeProduct
                          that.loading = false

                          that.updateSubscription(subscription).then(() => {
                            that.updateMembership(true).then(() => {
                              return that.addSilent({product}).then(() => {
                                console.log('added')

                                store.dispatch('alert/gift', i18n.t('Your subscription is now active') + '. ' + i18n.t('We have popped a FREE bundle in your basket') + '.' + i18n.t('Please checkout to claim') + '.', { root: true });
                                that.$emit('close-form')

                              })
                            })
                          })
                        }
                      })

                      break;

                    case 'processing':
                      //status.value = "Payment processing. We'll update you when payment is received.";
                      Sentry.captureMessage("Subscription confirmPayment status processing");
                      store.dispatch('alert', i18n.t("Payment processing. We'll update you when payment is received."), { root: true });
                      that.$emit('close-form')

                      break;

                    case 'requires_payment_method':
                      //status.value = 'Payment failed. Please try another payment method.';
                      // Redirect your user back to your payment page to attempt collecting
                      // payment again
                      that.loading = false
                      that.snackbar = true
                      that.snackColor = 'error'
                      that.snackText = 'Payment failed. Please try another payment method.'
                      break;

                    default:
                      //status.value = 'Something went wrong.';
                      that.loading = false
                      that.snackbar = true
                      that.snackColor = 'error'
                      that.snackText = 'Payment failed. Please try again.'
                      break;
                  }
                }
                else if (result.error) {
                  that.loading = false
                  that.snackbar = true
                  that.snackColor = 'error'
                  that.snackText = result.error.message
                }
              })
            },
            payOld() {
                this.stripeDialog = true

                let that = this

                stripeService.subscriptionIntent().then(intent => {

                    let elements = this.elements
                    this.stripe.confirmSetup(
                        {
                          elements,
                          redirect: 'if_required'
                        }
                    ).then(function(result) {
                        // Handle result.error or result.paymentIntent
                        console.log(result)

                        if (result.error) {
                            console.log(result.setupIntent)
                            this.error = true
                        } else {

                            let couponId = that.coupon ? that.coupon.id : null;

                            stripeService.confirmSubscription(result.setupIntent.payment_method, couponId, that.brand).then(response => {
                                console.log(response)
                                that.stripeDialog = false

                                //this.user = response.data.data

                                let subscription = response.data.stripe_subscription
                                let product = that.freeProduct

                                that.updateSubscription(subscription).then(() => {
                                    that.updateMembership(true).then(() => {
                                        return that.addSilent({product}).then(() => {
                                            console.log('added')

                                            store.dispatch('alert/gift', i18n.t('Your subscription is now active') + '. ' + i18n.t('We have popped a FREE bundle in your basket') + '.' + i18n.t('Please checkout to claim') + '.', { root: true });
                                            that.$emit('close-form')

                                        })
                                    })
                                })


                            })
                        }
                    }).catch(function (error) {
                        console.log(error)
                        that.stripeDialog = false
                    });
                })
            },
            clearCard () {
                this.$refs.subscriptionCard.clear() // "card" reference is defined in template above
            },
            renewSubscription() {
                let apiClient = axios.create({
                    baseURL: process.env.VUE_APP_NEOTAG_HOSTNAMES.split(",").includes(window.location.hostname) ? process.env.VUE_APP_API_HOST_NEOTAG : process.env.VUE_APP_API_HOST,
                    withCredentials: true,
                });

                apiClient.get('/sanctum/csrf-cookie').then(() => {
                    apiClient
                        .post(process.env.VUE_APP_NEOTAG_HOSTNAMES.split(",").includes(window.location.hostname) ? process.env.VUE_APP_API_HOST_NEOTAG : process.env.VUE_APP_API_HOST + '/api/stripe/renew-subscription')
                        .then(response => {
                            this.user = response.data.data

                            let subscription = response.data.data.stripe_subscription

                            this.updateSubscription(subscription).then(() => {
                                this.updateMembership(true).then(() => {
                                    // this.snack = true
                                    // this.snackColor = 'success'
                                    // this.snackText = 'Your membership has been renewed'
                                    store.dispatch('alert/success', i18n.t('Your membership has been renewed'), {root: true});
                                })
                            })
                        })
                        .catch(error => {
                            console.log(error)
                            if (error.response && [401, 403, 419].includes(error.response.status)) {
                                userService.logout().then(() => {
                                    store.dispatch('alert/error', i18n.t('You have been logged out due to inactivity'), { root: true });
                                })
                            }
                        })
                        .finally(() => this.show = true)
                })
            }
        }
    }
</script>

<style scoped>
    .stripe-card {
        /*width: 300px;*/
        border: 1px solid grey !important;
        box-sizing: border-box;

        height: 40px;

        padding: 10px 12px;
        margin: 20px 0;

        border: 1px solid transparent;
        border-radius: 4px;
        background-color: white;

        box-shadow: 0 1px 3px 0 #e6ebf1;
        -webkit-transition: box-shadow 150ms ease;
        transition: box-shadow 150ms ease;
    }
    .stripe-card.complete {
        border-color: green !important;
    }
    .stripe-card.complete.declined {
        border-color: red !important;
    }

    .stripe-card--focus {
        box-shadow: 0 1px 3px 0 #cfd7df;
    }

    .stripe-card--invalid {
        border-color: #fa755a;
    }

    .stripe-card--webkit-autofill {
        background-color: #fefde5 !important;
    }

    .pay-with-stripe {
        display: block;
        background: #666ee8;
        color: #000;
        box-shadow: 0 4px 6px rgba(50, 50, 93, 0.11), 0 1px 3px rgba(0, 0, 0, 0.08);
        border-radius: 4px;
        border: 0;
        font-weight: 700;
        width: 100%;
        height: 40px;
        outline: none;
        cursor: pointer;
        transition: all 0.15s ease;
    }

    .pay-with-stripe:focus {
        background: #555abf;
    }

    .pay-with-stripe:hover {
        transform: translateY(-1px);
        box-shadow: 0 7px 14px 0 rgba(50, 50, 93, 0.1),
        0 3px 6px 0 rgba(0, 0, 0, 0.08);
    }

    .clear-button {
        margin-top: 20px;
    }
</style>
